import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toBlob } from "html-to-image";
import { ArrowDownTrayIcon, XMarkIcon } from "@heroicons/react/24/outline";
import AutoFitText from "./AutoFitText";
import { UserContext } from "../App";

const EMPTY_VALUE_LABEL = "-";
const MAX_CARD_NOTES_LENGTH = 260;

function getFileName(cardKey, prefix) {
  const safePrefix = prefix || "day-card";
  if (!cardKey) return `${safePrefix}.png`;
  return `${safePrefix}-${cardKey}.png`;
}

function getPnlTone(pnl) {
  if (pnl > 0) return "positive";
  if (pnl < 0) return "negative";
  return "flat";
}

function getIntegerDigits(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 1;
  const abs = Math.abs(numeric);
  if (abs < 1) return 1;
  return Math.floor(Math.log10(abs)) + 1;
}

function getScoreCardPnlFontBounds(pnlValue) {
  const digits = getIntegerDigits(pnlValue);

  if (digits >= 8) return { maxPx: 46, minPx: 28 };
  if (digits >= 6) return { maxPx: 56, minPx: 34 };
  if (digits >= 4) return { maxPx: 66, minPx: 40 };
  return { maxPx: 74, minPx: 48 };
}

function formatPercentageLabel(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return EMPTY_VALUE_LABEL;
  }
  const parsed = Number(value);
  if (parsed >= 999999) return "New";
  return `${parsed > 0 ? "+" : ""}${parsed.toFixed(1)}%`;
}

function formatRrLabel(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return EMPTY_VALUE_LABEL;
  }
  return `${Number(value).toFixed(2)}R`;
}

function formatTextLabel(value, formatter) {
  if (value === null || value === undefined) return EMPTY_VALUE_LABEL;
  const parsed = String(value).trim();
  if (!parsed) return EMPTY_VALUE_LABEL;
  return formatter ? formatter(parsed) : parsed;
}

function buildDefaultStatCards({ percentage, rr, session, grade }) {
  return [
    { label: "Performance", value: formatPercentageLabel(percentage) },
    { label: "R:R", value: formatRrLabel(rr) },
    { label: "Session", value: formatTextLabel(session) },
    { label: "Grade", value: formatTextLabel(grade, (value) => value.toUpperCase()) },
  ];
}

function getScoreCardNoteDocId(accountId, cardType, dateKey) {
  if (!accountId || !cardType || !dateKey) return "";
  return `scoreCardNote__${accountId}__${cardType}__${dateKey}`;
}

function getMonthRangeLabel(cardType, dateKey) {
  if (cardType !== "month" || !dateKey) return "";

  const match = /^(\d{4})-(\d{2})$/.exec(dateKey);
  if (!match) return "";

  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return "";

  const daysInMonth = new Date(year, month, 0).getDate();

  return `1-${daysInMonth}, ${year}`;
}

export default function DayScoreCardModal({
  isOpen,
  onClose,
  dateKey,
  dateLabel,
  pnl = 0,
  rr = null,
  percentage = null,
  session = null,
  grade = null,
  cardType = "day",
  heading,
  metricLabel,
  statCards = null,
  notesLabel,
  notesPlaceholder,
  initialNotes = "",
  fileNamePrefix,
}) {
  const { currentUser, selectedAccount } = useContext(UserContext) || {};
  const [cardTheme, setCardTheme] = useState("dark");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [cardNotes, setCardNotes] = useState(initialNotes || "");
  const [isNotesLoading, setIsNotesLoading] = useState(false);
  const [isNotesSaving, setIsNotesSaving] = useState(false);
  const [notesError, setNotesError] = useState("");
  const cardRef = useRef(null);
  const hydratedCardKeyRef = useRef("");
  const lastPersistedNotesRef = useRef(initialNotes || "");
  const notesSaveTimeoutRef = useRef(null);
  const wasOpenRef = useRef(false);

  const modalTitle = heading || (cardType === "month" ? "Month Score Card" : "Day Score Card");
  const metricLabelText = metricLabel || (cardType === "month" ? "Net month PnL" : "Net day PnL");
  const noteLabelText = notesLabel || (cardType === "month" ? "Month notes" : "Day notes");
  const monthRangeLabel = getMonthRangeLabel(cardType, dateKey);
  const notePlaceholderText =
    notesPlaceholder ||
    (cardType === "month"
      ? "Add a clean note for this month card..."
      : "Add a clean note for this day card...");
  const downloadPrefix = fileNamePrefix || (cardType === "month" ? "month-card" : "day-card");

  const pnlTone = useMemo(() => getPnlTone(Number(pnl) || 0), [pnl]);

  const formattedPnl = useMemo(() => {
    const amount = Number(pnl) || 0;
    return `${amount > 0 ? "+" : amount < 0 ? "-" : ""}$${Math.abs(amount).toFixed(2)}`;
  }, [pnl]);

  const pnlFontBounds = useMemo(() => getScoreCardPnlFontBounds(pnl), [pnl]);

  const resolvedStatCards = useMemo(() => {
    const sourceCards =
      Array.isArray(statCards) && statCards.length
        ? statCards
        : buildDefaultStatCards({ percentage, rr, session, grade });

    return sourceCards.map((item) => ({
      label: item?.label || "Stat",
      value:
        item?.value === null || item?.value === undefined || String(item.value).trim() === ""
          ? EMPTY_VALUE_LABEL
          : String(item.value),
    }));
  }, [grade, percentage, rr, session, statCards]);

  const trimmedCardNotes = cardNotes.trim();
  const isCardDark = cardTheme === "dark";
  const noteDocId = useMemo(
    () => getScoreCardNoteDocId(selectedAccount?.id, cardType, dateKey),
    [cardType, dateKey, selectedAccount?.id]
  );
  const notesHelperText = useMemo(() => {
    if (notesError) return notesError;
    if (isNotesLoading) return "Loading saved note...";
    if (isNotesSaving) return "Saving note...";
    return "Notes render directly onto the saved card image and save automatically.";
  }, [isNotesLoading, isNotesSaving, notesError]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEsc = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const currentCardKey = `${downloadPrefix}:${currentUser?.uid || "guest"}:${selectedAccount?.id || "no-account"}:${dateKey || dateLabel || modalTitle}`;
    if (!currentCardKey) return;
    if (hydratedCardKeyRef.current === currentCardKey) return;

    hydratedCardKeyRef.current = currentCardKey;
    let isCancelled = false;

    const hydrateNotes = async () => {
      const fallbackNotes = initialNotes || "";

      if (!currentUser?.uid || !noteDocId) {
        lastPersistedNotesRef.current = fallbackNotes;
        setCardNotes(fallbackNotes);
        setNotesError("");
        setIsNotesLoading(false);
        return;
      }

      setIsNotesLoading(true);
      setNotesError("");

      try {
        const { db } = await import("../firebase");
        const { doc, getDoc } = await import("firebase/firestore");
        const noteRef = doc(db, "users", currentUser.uid, "preferences", noteDocId);
        const noteSnap = await getDoc(noteRef);
        const nextNotes = noteSnap.exists() ? String(noteSnap.data()?.notes || "") : fallbackNotes;

        if (isCancelled) return;
        lastPersistedNotesRef.current = nextNotes;
        setCardNotes(nextNotes);
      } catch (error) {
        console.error(`Failed to load ${cardType} card notes:`, error);
        if (isCancelled) return;
        lastPersistedNotesRef.current = fallbackNotes;
        setCardNotes(fallbackNotes);
        setNotesError("Could not load saved note.");
      } finally {
        if (!isCancelled) {
          setIsNotesLoading(false);
        }
      }
    };

    hydrateNotes();

    return () => {
      isCancelled = true;
    };
  }, [
    cardType,
    currentUser?.uid,
    dateKey,
    dateLabel,
    downloadPrefix,
    initialNotes,
    isOpen,
    modalTitle,
    noteDocId,
    selectedAccount?.id,
  ]);

  useEffect(() => {
    const persistNotes = async (nextNotes) => {
      if (!currentUser?.uid || !noteDocId || !selectedAccount?.id) {
        lastPersistedNotesRef.current = nextNotes;
        return;
      }

      setIsNotesSaving(true);

      try {
        const { db } = await import("../firebase");
        const { deleteDoc, doc, setDoc } = await import("firebase/firestore");
        const noteRef = doc(db, "users", currentUser.uid, "preferences", noteDocId);
        const trimmedNotes = nextNotes.trim();

        if (trimmedNotes) {
          await setDoc(noteRef, {
            type: "scoreCardNote",
            accountId: selectedAccount.id,
            cardType,
            dateKey,
            dateLabel: dateLabel || "",
            notes: nextNotes,
            updatedAt: new Date().toISOString(),
          }, { merge: true });
        } else {
          await deleteDoc(noteRef);
        }

        lastPersistedNotesRef.current = nextNotes;
        setNotesError("");
      } catch (error) {
        console.error(`Failed to save ${cardType} card notes:`, error);
        setNotesError("Could not save note.");
      } finally {
        setIsNotesSaving(false);
      }
    };

    const wasOpen = wasOpenRef.current;

    if (wasOpen && !isOpen && cardNotes !== lastPersistedNotesRef.current) {
      if (notesSaveTimeoutRef.current) {
        clearTimeout(notesSaveTimeoutRef.current);
        notesSaveTimeoutRef.current = null;
      }
      void persistNotes(cardNotes);
    }

    wasOpenRef.current = isOpen;

    if (!isOpen || isNotesLoading || cardNotes === lastPersistedNotesRef.current) {
      return undefined;
    }

    if (notesSaveTimeoutRef.current) {
      clearTimeout(notesSaveTimeoutRef.current);
    }

    notesSaveTimeoutRef.current = setTimeout(() => {
      void persistNotes(cardNotes);
    }, 450);

    return () => {
      if (notesSaveTimeoutRef.current) {
        clearTimeout(notesSaveTimeoutRef.current);
        notesSaveTimeoutRef.current = null;
      }
    };
  }, [
    cardNotes,
    cardType,
    currentUser?.uid,
    dateKey,
    dateLabel,
    isNotesLoading,
    isOpen,
    noteDocId,
    selectedAccount?.id,
  ]);

  if (!isOpen || typeof document === "undefined") return null;

  const handleSaveImage = async () => {
    if (!cardRef.current || isSaving) return;
    setSaveError("");
    setIsSaving(true);

    try {
      const backgroundColor = isCardDark ? "#070F1C" : "#F8FAFC";
      const blob = await toBlob(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor,
      });

      if (!blob) throw new Error("Could not generate image.");

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = getFileName(dateKey, downloadPrefix);
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Failed to save ${cardType} card image:`, error);
      setSaveError("Could not save image. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const pnlColorClass =
    pnlTone === "positive"
      ? "theme-success"
      : pnlTone === "negative"
        ? "theme-loss"
        : isCardDark
          ? "text-[#E2E8F0]"
          : "text-[#334155]";

  const cardTextMutedClass = isCardDark ? "text-[#8EA0BC]" : "text-[#64748B]";
  const cardTextStrongClass = isCardDark ? "text-[#F8FAFC]" : "text-[#0F172A]";
  const statCardClass = isCardDark
    ? "bg-white/[0.03] border-white/[0.10]"
    : "bg-white/70 border-[#D9E1EC]";

  const modalShellClass = isCardDark
    ? "border-[#1E293B] bg-[#0B1220]/95"
    : "border-[#D8E0EA] bg-white/95";

  const toggleShellClass = isCardDark
    ? "border-[#334155] bg-[#0F172A]/90"
    : "border-[#D8E0EA] bg-white/90";

  const inactiveToggleTextClass = isCardDark ? "text-[#94A3B8]" : "text-[#64748B]";
  const closeButtonClass = isCardDark
    ? "border-[#334155] text-[#94A3B8] hover:text-white hover:bg-white/[0.06]"
    : "border-[#D8E0EA] text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8FAFC]";

  const saveButtonClass = isCardDark
    ? "bg-white text-[#0F172A] hover:opacity-90"
    : "bg-[#0F172A] text-white hover:opacity-90";

  const notesFieldClass = isCardDark
    ? "border-[#334155] bg-[#0F172A]/70 text-[#F8FAFC] placeholder:text-[#64748B]"
    : "border-[#D8E0EA] bg-white text-[#0F172A] placeholder:text-[#94A3B8]";

  const notesPanelClass = isCardDark
    ? "border-[#1E293B] bg-[#0F172A]/58"
    : "border-[#D8E0EA] bg-[#F8FAFC]/90";

  const notesFooterBorderClass = isCardDark ? "border-white/[0.08]" : "border-[#D8E0EA]";

  const cardBackgroundStyle = isCardDark
    ? {
        backgroundImage: `
          radial-gradient(circle at top right, rgba(56, 189, 248, 0.14), transparent 24%),
          radial-gradient(circle at bottom left, rgba(34, 197, 94, 0.12), transparent 22%),
          linear-gradient(180deg, #061024 0%, #081327 52%, #070F1C 100%)
        `,
        boxShadow: "0 28px 80px rgba(2, 6, 23, 0.42)",
      }
    : {
        backgroundImage: `
          radial-gradient(circle at top right, rgba(56, 189, 248, 0.10), transparent 24%),
          radial-gradient(circle at bottom left, rgba(34, 197, 94, 0.08), transparent 22%),
          linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)
        `,
        boxShadow: "0 28px 80px rgba(15, 23, 42, 0.10)",
      };

  const sheenStyle = isCardDark
    ? {
        background:
          "linear-gradient(118deg, transparent 0%, transparent 44%, rgba(255,255,255,0.05) 52%, transparent 60%, transparent 100%)",
      }
    : {
        background:
          "linear-gradient(118deg, transparent 0%, transparent 44%, rgba(255,255,255,0.42) 52%, transparent 60%, transparent 100%)",
      };

  return createPortal(
    <div
      className="fixed inset-0 z-[120] bg-black/75 backdrop-blur-md flex items-start justify-center overflow-y-auto p-3 sm:p-4 lg:items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={modalTitle}
    >
      <div
        className={`my-3 w-full max-w-[1240px] rounded-[28px] border p-4 shadow-2xl sm:my-5 sm:p-6 md:p-7 ${modalShellClass}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-6 flex items-center justify-between gap-3 sm:mb-7">
          <h2 className={`text-xl sm:text-2xl font-semibold ${cardTextStrongClass}`}>{modalTitle}</h2>

          <div className="flex items-center gap-3">
            <div className={`inline-flex items-center rounded-full border p-1 ${toggleShellClass}`}>
              <button
                type="button"
                onClick={() => setCardTheme("light")}
                className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                  cardTheme === "light" ? "bg-[#0F172A] text-white" : inactiveToggleTextClass
                }`}
              >
                Light
              </button>
              <button
                type="button"
                onClick={() => setCardTheme("dark")}
                className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                  cardTheme === "dark" ? "bg-[#0F172A] text-white" : inactiveToggleTextClass
                }`}
              >
                Dark
              </button>
            </div>

            <button
              type="button"
              onClick={onClose}
              className={`w-10 h-10 rounded-full border transition-colors flex items-center justify-center ${closeButtonClass}`}
              aria-label={`Close ${modalTitle.toLowerCase()}`}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,470px)_minmax(320px,1fr)] lg:items-start lg:gap-7 xl:gap-8">
          <div className="flex items-center justify-center lg:justify-start">
            <div
              ref={cardRef}
              className="relative w-full max-w-[470px] overflow-hidden rounded-[34px] border px-6 py-7 sm:px-7 sm:py-8 lg:px-8 lg:py-9"
              style={{
                ...cardBackgroundStyle,
                borderColor: isCardDark ? "rgba(255,255,255,0.08)" : "#D6DFEA",
              }}
            >
              <div className="absolute inset-0 pointer-events-none" style={sheenStyle} />
              <div className="relative z-10">
                <div className={`text-[11px] uppercase tracking-[0.28em] font-semibold ${cardTextMutedClass}`}>
                  Trading Journal
                </div>

                <div className="mt-4 flex max-w-[360px] flex-wrap items-end gap-x-3 gap-y-2">
                  <div className={`text-[25px] sm:text-[27px] leading-[1.02] font-bold tracking-[-0.03em] ${cardTextStrongClass}`}>
                    {dateLabel}
                  </div>

                  {monthRangeLabel && (
                    <div className={`pb-[2px] text-[15px] sm:text-[16px] font-semibold tracking-[0.01em] ${cardTextStrongClass}`}>
                      {monthRangeLabel}
                    </div>
                  )}
                </div>

                <AutoFitText
                  minPx={pnlFontBounds.minPx}
                  maxPx={pnlFontBounds.maxPx}
                  className={`${monthRangeLabel ? "mt-9" : "mt-10"} w-full max-w-[372px]`}
                  textClassName={`font-black tracking-[-0.05em] ${pnlColorClass}`}
                >
                  {formattedPnl}
                </AutoFitText>

                <div className={`mt-2 text-[15px] ${cardTextMutedClass}`}>
                  {metricLabelText}
                </div>

                <div className="mt-9 grid grid-cols-2 gap-4 sm:mt-10 sm:gap-5">
                  {resolvedStatCards.map((item) => (
                    <div
                      key={item.label}
                      className={`flex min-h-[126px] flex-col justify-between gap-6 rounded-[26px] border px-5 py-4 sm:min-h-[132px] sm:px-6 sm:py-5 ${statCardClass}`}
                    >
                      <div className={`text-[11px] uppercase tracking-[0.18em] ${cardTextMutedClass}`}>
                        {item.label}
                      </div>

                      <div
                        className={`break-words text-[19px] font-semibold leading-snug tracking-[-0.02em] sm:text-[20px] ${cardTextStrongClass}`}
                      >
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>

                {trimmedCardNotes && (
                  <div className={`mt-5 rounded-[26px] border p-5 sm:p-6 ${statCardClass}`}>
                    <div className={`text-[11px] uppercase tracking-[0.18em] ${cardTextMutedClass}`}>
                      {noteLabelText}
                    </div>
                    <div
                      className={`mt-3 text-[14px] leading-[1.65] font-medium whitespace-pre-wrap break-words ${cardTextStrongClass}`}
                    >
                      {trimmedCardNotes}
                    </div>
                  </div>
                )}
              </div>

              <div
                className={`absolute bottom-2 left-0 right-0 text-center text-[10px] tracking-[0.15em] uppercase font-medium pointer-events-none ${
                  isCardDark ? "text-white/[0.20]" : "text-slate-400/30"
                }`}
              >
                BandzJournal
              </div>
            </div>
          </div>

          <div className={`rounded-[28px] border p-5 sm:p-6 lg:min-h-full ${notesPanelClass}`}>
            <div className="flex h-full flex-col">
              <div>
                <label className={`block text-sm font-medium ${cardTextStrongClass}`}>{noteLabelText}</label>
                <textarea
                  value={cardNotes}
                  onChange={(event) => setCardNotes(event.target.value.slice(0, MAX_CARD_NOTES_LENGTH))}
                  className={`mt-4 w-full min-h-[160px] rounded-[22px] border px-4 py-4 text-[15px] leading-relaxed tracking-[-0.01em] resize-y focus:outline-none focus:ring-2 focus:ring-[#3B82F6] sm:min-h-[180px] sm:px-5 lg:min-h-[440px] lg:resize-none ${notesFieldClass}`}
                  placeholder={notePlaceholderText}
                />
                <div className={`mt-3 flex flex-col gap-2 text-xs sm:flex-row sm:items-start sm:justify-between sm:gap-4 ${cardTextMutedClass}`}>
                  <span
                    className={`max-w-[42rem] leading-relaxed ${
                      notesError ? "text-[#DC2626] dark:text-[#FF6B6B]" : ""
                    }`}
                  >
                    {notesHelperText}
                  </span>
                  <span className="shrink-0 text-right sm:pt-[1px]">
                    {cardNotes.length}/{MAX_CARD_NOTES_LENGTH}
                  </span>
                </div>
              </div>

              <div className={`mt-6 flex items-center justify-start gap-3 border-t pt-5 lg:mt-auto ${notesFooterBorderClass}`}>
                <button
                  type="button"
                  onClick={handleSaveImage}
                  disabled={isSaving}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-opacity disabled:opacity-60 ${saveButtonClass}`}
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {isSaving ? "Saving..." : "Save image"}
                </button>
              </div>

              {saveError && (
                <div className="mt-3 text-sm text-[#DC2626] dark:text-[#FF6B6B]">{saveError}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
